import { InlineNotification } from '@carbon/react';
import type { MapRef } from 'react-map-gl';
import { useMapZoomBelow } from 'components/map/use-map-zoom';
import type { ScreenPoint } from './types';
import { SURVEY_PICK_MIN_ZOOM } from './utils';
import styles from './create-area-map-overlays.module.scss';

export interface CreateAreaMapOverlaysProps {
  mapRef: MapRef | null;
  /** Index of the polygon being edited, if any. */
  editingIndex?: number | null;
  /** From {@link useAddVertexPoint}. */
  addVertexPoint: ScreenPoint | null;
  /** True while survey rows are on screen to receive a pick. */
  surveyPicking: boolean;
  snapToGrid: boolean;
  /** True while the map is the panel's workspace — drawing or picking. */
  workspace: boolean;
}

/**
 * The panel's notes over the map: which polygon is being edited, the add-vertex tip,
 * and the zoom hints for picking and snapping. Rendered over the map, inside the
 * host's positioned container, not inside `<Map>`.
 *
 * @param {CreateAreaMapOverlaysProps} props - The panel's map state.
 * @return {JSX.Element} The overlays that apply right now.
 */
export function CreateAreaMapOverlays({
  mapRef,
  editingIndex,
  addVertexPoint,
  surveyPicking,
  snapToGrid,
  workspace,
}: CreateAreaMapOverlaysProps): JSX.Element {
  // Zoomed out past the tile's range there are no sections to click or snap to,
  // so the map says what to do about it rather than leaving clicks inert. Follows
  // the live zoom, since the user moves the map after it is framed.
  const gridTooFarOut = useMapZoomBelow(mapRef, SURVEY_PICK_MIN_ZOOM);

  // Each hint belongs to a tool that is actually in use. The pick handler is only
  // registered while the survey rows are on screen, but snapping is a setting that
  // defaults on, so the snap hint used to advertise a zoom for a tool nobody had
  // opened (UAT). `workspace` marks the map as the panel's, and without a pick
  // handler that means drawing.
  const surveyTooFarOut = surveyPicking && gridTooFarOut;
  const snapTooFarOut =
    snapToGrid && workspace && !surveyPicking && gridTooFarOut;
  const editing = editingIndex !== null && editingIndex !== undefined;

  return (
    <>
      {/* Says which polygon the vertex editing applies to (UAT) — entered from
          the panel's pencil or by clicking the shape; goes away when the edit
          does (Done, clicking off the shape, deleting it, leaving the method). */}
      {editing && (
        <div className={styles.editBanner}>
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={`Currently editing Polygon ${editingIndex + 1}`}
            subtitle="Drag a corner or the shape, or click its edge to add a corner."
          />
        </div>
      )}
      {/* The unit builder's add-vertex affordance: a green plus on the line
          under the pointer, labelled, so the click target explains itself. */}
      {editing && addVertexPoint && (
        <div
          className={styles.addVertexTip}
          style={{ left: addVertexPoint.x, top: addVertexPoint.y }}
          aria-hidden
        >
          Click to add a vertex
        </div>
      )}
      {surveyTooFarOut && (
        <div className={styles.zoomHint} aria-live="polite">
          Zoom in to show and select survey sections.
        </div>
      )}
      {snapTooFarOut && (
        <div className={styles.zoomHint} aria-live="polite">
          Zoom in to snap vertices to the survey grid.
        </div>
      )}
    </>
  );
}
